import {
  type ArtworkSet,
  type ArtworkTint,
  type LandingMedia,
  landingVideoSet,
  type TimedArtworkTint,
} from "@pax/api-contract/presets/v1alpha1";
import { cn } from "@pax/ui";
import { useCallback, useEffect, useRef } from "react";
import { LoopingVideo } from "@/features/video/components/LoopingVideo";
import type { VideoDecoderPreference } from "@/features/video/lib/playback";
import { largestSource, srcSet } from "@/lib/images";

export function firstLandingTint(landing: LandingMedia | null | undefined): ArtworkTint | null {
  const first = landing?.tintTrack[0];
  return first ? { hue: first.hue, chroma: first.chroma } : null;
}

/**
 * A preset's landing artwork, and its video looping over it while `play`. `decoder` is `native`
 * where several previews can play in turn, so only the preset's own page decodes in software.
 */
export function LandingArtwork({
  landing,
  fallback,
  play = true,
  priority = false,
  decoder = "native",
  className,
  onReady,
  onTint,
}: {
  landing: LandingMedia | null | undefined;
  fallback?: ArtworkSet | null;
  play?: boolean;
  priority?: boolean;
  decoder?: VideoDecoderPreference;
  className?: string;
  onReady?: () => void;
  onTint?: (tint: ArtworkTint) => void;
}) {
  const image = landing?.poster ?? fallback;
  const video = landing?.video;
  const showTintAt = useTimedTint(landing?.tintTrack, onTint);

  if (!image) return <div className={className} />;

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <img
        src={largestSource(image)}
        srcSet={srcSet(image)}
        sizes="100vw"
        alt=""
        fetchPriority={priority ? "high" : "auto"}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={onReady}
        className="absolute inset-0 size-full object-cover"
      />
      {video && play && (
        <LoopingVideo
          video={landingVideoSet(video)}
          decoder={decoder}
          onFrame={onTint && showTintAt}
          className="absolute inset-0"
        />
      )}
    </div>
  );
}

/** Reports the track's first tint, then the tint of each frame as the video shows it. */
function useTimedTint(
  track: TimedArtworkTint[] | undefined,
  onTint: ((tint: ArtworkTint) => void) | undefined,
) {
  const onTintRef = useRef(onTint);
  onTintRef.current = onTint;
  const shown = useRef(-1);

  useEffect(() => {
    shown.current = 0;
    const first = track?.[0];
    if (first) onTintRef.current?.({ hue: first.hue, chroma: first.chroma });
  }, [track]);

  return useCallback(
    (mediaTime: number) => {
      if (!track?.length) return;
      const atMs = mediaTime * 1_000;
      let next = 0;
      while (next + 1 < track.length && (track[next + 1]?.atMs ?? Infinity) <= atMs) next += 1;
      if (next === shown.current) return;
      shown.current = next;
      const tint = track[next];
      if (tint) onTintRef.current?.({ hue: tint.hue, chroma: tint.chroma });
    },
    [track],
  );
}
